import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { strings } from '@singparty/shared';
import { sessionApi } from '@/lib/sessionApi';
import { ApiClientError } from '@/lib/apiClient';
import { Logo } from '@/components/ui/Logo';

/**
 * TV pairing screen (Path B, 05 §2.7). Shows a 4-char code; the host links it
 * from the console; we poll and, once linked, the server sets sp_screen and we
 * navigate to /tv/{sid}. Keep on-screen text under three lines.
 */
export const Route = createFileRoute('/tv/')({
  component: TvPairing,
});

function TvPairing() {
  const navigate = useNavigate();
  const [pair, setPair] = useState<{ pairCode: string; pairSecret: string } | null>(null);
  const [offline, setOffline] = useState(false);
  const [remainingS, setRemainingS] = useState(0);
  const pairRef = useRef(pair);
  pairRef.current = pair;

  // Acquire (and re-acquire) a pair code.
  useEffect(() => {
    let cancelled = false;
    async function acquire() {
      try {
        const data = await sessionApi.pairStart();
        if (cancelled) return;
        setPair({ pairCode: data.pairCode, pairSecret: data.pairSecret });
        setRemainingS(data.expiresInS);
        setOffline(false);
      } catch {
        if (cancelled) return;
        setOffline(true);
        setTimeout(acquire, 3000);
      }
    }
    void acquire();
    return () => {
      cancelled = true;
    };
  }, []);

  // Countdown; refresh code on expiry.
  useEffect(() => {
    if (!pair) return;
    const t = setInterval(() => {
      setRemainingS((s) => {
        if (s <= 1) {
          void sessionApi
            .pairStart()
            .then((data) => {
              setPair({ pairCode: data.pairCode, pairSecret: data.pairSecret });
              setRemainingS(data.expiresInS);
            })
            .catch(() => setOffline(true));
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [pair]);

  // Poll for linking.
  useEffect(() => {
    const t = setInterval(async () => {
      const p = pairRef.current;
      if (!p) return;
      try {
        const status = await sessionApi.pairStatus(p.pairCode, p.pairSecret);
        if (status.linked) {
          clearInterval(t);
          await navigate({ to: '/tv/$sid', params: { sid: status.sessionId } });
        }
      } catch (err) {
        // 404 → code expired/unknown; the countdown effect will refresh it.
        if (!(err instanceof ApiClientError && err.status === 404)) setOffline(true);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [navigate]);

  const mmss = `${Math.floor(remainingS / 60)}:${String(remainingS % 60).padStart(2, '0')}`;

  return (
    <div className="tv-theme flex min-h-full flex-col items-center justify-center bg-bg px-8 text-center">
      <Logo className="mb-8 w-105" />

      {offline ? (
        <p className="text-2xl text-danger">{strings.tv.pairOffline}…</p>
      ) : pair ? (
        <>
          <div className="mb-6 font-mono text-[clamp(5rem,20vw,12rem)] font-extrabold leading-none tracking-[0.1em] text-primary">
            {pair.pairCode}
          </div>
          <p className="mb-2 max-w-2xl text-2xl">{strings.tv.pairInstruction}</p>
          <p className="text-lg text-text-secondary">{strings.tv.pairRefreshIn(mmss)}</p>
        </>
      ) : (
        <p className="text-2xl text-text-secondary">…</p>
      )}
    </div>
  );
}
